'use client'

import { BannerDisplayType, WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import { useMemo } from 'react'

import { PLBaseErrorBoundary } from '@src/components/pl-base-error-boundary'
import { PLTypedWidgetInterface, PLTypedWidgetProps } from '@src/components/pl-base-widget'
import { useWidgetSectionPosition } from '@src/components/pl-base-widget/contexts/widget-page-order'
import { usePLPageMetaOptional } from '@src/components/pl-page-context'
import NAVIGATION_TYPE from '@src/quince-site-common/src/constants/navigationType'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import CUSTOM_SECTION_TYPE from '@src/quince-site-common/src/domains/CustomSectionType'
import HeroBanner from '@src/SectionRenderer/sections/heroBanner'
import HeroCarousel from '@src/SectionRenderer/sections/heroCarousel/HeroCarousel'
import { MultipleHeroBanners } from '@src/SectionRenderer/sections/MultipleHeroBanners'

import { buildHeroBannerConfig, hasBannerContent, makeSection } from '../banner-section-builders'
import { PLSectionTrackingWrapper } from '../pl-section-tracking-wrapper'
import { buildWidgetTrackingMetaData, buildWidgetTrackingTypes } from '../widget-utils'

import type {
  CollectionBannerWidgetData,
  HomepageBannerWidgetData,
} from '@lastbrand/storefront-proto-registry/api/v1/widget'

function toNavigationType(navigationType?: string): NAVIGATION_TYPE | null {
  if (navigationType && navigationType in NAVIGATION_TYPE) {
    return NAVIGATION_TYPE[navigationType as keyof typeof NAVIGATION_TYPE]
  }
  return null
}

/**
 * Build the parent container `CustomSection` (HERO_CAROUSEL / MULTIPLE_HERO_BANNERS) whose `subSections`
 * are the per-slide hero banners. Slides reuse the shared `buildHeroBannerConfig` (same conversion the
 * COLLECTION_BANNER widget uses); `makeSection` supplies the class-name/array defaults. The existing
 * container components arrange the sub-banners and own per-slide analytics (horizontalPosition) +
 * DS/legacy routing — same contract the BFF home page used.
 *
 * `tracker` mirrors legacy's `SectionRenderer`-injected `home_page` tracker: `MultipleHeroBanners`/
 * `HeroCarousel` read `sectionConfig.tracker` and override each subSection's tracker per-slide
 * (`${tracker}__heroBanner_${idx}` / `${tracker}__heroCarousel`), so it only needs to land on the
 * container here — the per-slide override happens downstream in those components.
 *
 * `sectionPosition` is the derived 1-based page position of this widget (`useWidgetSectionPosition`).
 * Legacy's BFF pre-bakes the container's page position into the container context AND every
 * subsection's context (slide-level clicks read the slide's own context), so the same value is
 * passed to each slide's builder. When it's unavailable (no provider) nothing is emitted — the wire
 * `contentContainerPosition` is a 0-based banner ordinal, not a page position, so it is never used
 * as a fallback (see `buildWidgetTrackingMetaData`).
 */
function buildContainerSection(
  data: HomepageBannerWidgetData,
  sectionType: CUSTOM_SECTION_TYPE,
  slides: CollectionBannerWidgetData[],
  tracker?: string,
  sectionPosition?: number,
): CustomSection {
  return makeSection({
    id: data.id,
    displayTitle: data.displayTitle,
    title: data.displayTitle,
    sectionType,
    tracker,
    elementPosition: sectionPosition,
    subSections: slides.map(slide => buildHeroBannerConfig(slide, tracker, sectionPosition)),
    navigationType: toNavigationType(data.navigationType),
    trackingMetaData: buildWidgetTrackingMetaData(data.analytics, sectionPosition),
    // Arms the PL section-level IS_VISIBLE/CLICK emitter (PLSectionTrackingWrapper) once the wire
    // ships trackingTypes — legacy sends ['VISIBLE'] for every home section (proto 3.20.0).
    trackingTypes: buildWidgetTrackingTypes(data.analytics),
  })
}

const HomepageBannerWidgetComponent = (props: PLTypedWidgetProps<HomepageBannerWidgetData>) => {
  const bannerData = props.data.homepageBannerData
  const tracker = usePLPageMetaOptional()?.sectionTracker
  const sectionPosition = useWidgetSectionPosition(props.id)

  // Memoized because subscribing to the page-order context re-renders this widget on every
  // layout-pagination append (home drains eagerly), and an unmemoized build would hand the
  // carousel/multi-banner components a fresh `sectionConfig` + `subSections` identity each time.
  const sectionConfig = useMemo(() => {
    const slides = (Array.isArray(bannerData?.slides) ? bannerData.slides : []).filter(hasBannerContent)
    const [firstSlide] = slides
    if (!bannerData || !firstSlide) return null

    switch (bannerData.displayType) {
      case BannerDisplayType.BANNER_DISPLAY_CAROUSEL:
        return buildContainerSection(bannerData, CUSTOM_SECTION_TYPE.HERO_CAROUSEL, slides, tracker, sectionPosition)
      case BannerDisplayType.BANNER_DISPLAY_MULTIPLE:
        return buildContainerSection(
          bannerData,
          CUSTOM_SECTION_TYPE.MULTIPLE_HERO_BANNERS,
          slides,
          tracker,
          sectionPosition,
        )
      case BannerDisplayType.BANNER_DISPLAY_SINGLE:
      case BannerDisplayType.BANNER_DISPLAY_TYPE_UNSPECIFIED:
      case BannerDisplayType.UNRECOGNIZED:
      default:
        // SINGLE (and any unexpected value): render the first slide as a standalone hero banner,
        // identical to how the COLLECTION_BANNER widget renders a single HERO_BANNER.
        return buildHeroBannerConfig(firstSlide, tracker, sectionPosition)
    }
  }, [bannerData, tracker, sectionPosition])

  if (!bannerData || !sectionConfig) return null

  // The memoized sectionConfig above is already the display-type-specific section (container for
  // CAROUSEL/MULTIPLE, first-slide hero for SINGLE); the switch only picks the component. The
  // tracking wrapper mounts the same section-level IS_VISIBLE/CLICK emitter legacy's
  // withCustomSectionWrapper mounted around each of these three section types.
  const banner = (() => {
    switch (bannerData.displayType) {
      case BannerDisplayType.BANNER_DISPLAY_CAROUSEL:
        return <HeroCarousel sectionConfig={sectionConfig} />
      case BannerDisplayType.BANNER_DISPLAY_MULTIPLE:
        return <MultipleHeroBanners sectionConfig={sectionConfig} />
      case BannerDisplayType.BANNER_DISPLAY_SINGLE:
      case BannerDisplayType.BANNER_DISPLAY_TYPE_UNSPECIFIED:
      case BannerDisplayType.UNRECOGNIZED:
      default:
        return <HeroBanner sectionConfig={sectionConfig} />
    }
  })()

  return (
    <PLSectionTrackingWrapper sectionConfig={sectionConfig} tracker={tracker}>
      {banner}
    </PLSectionTrackingWrapper>
  )
}

export const HomepageBannerWidget: PLTypedWidgetInterface<HomepageBannerWidgetData, WidgetType.HOMEPAGE_BANNER> = {
  id: WidgetType.HOMEPAGE_BANNER,
  errorBoundary: PLBaseErrorBoundary,
  render: HomepageBannerWidgetComponent,
}
